import React, { memo, useCallback, useEffect, useState } from 'react';

import { Box, Typography } from '@mui/material';
import {
  Focus,
  GetProvidersEligibilityDataResponse,
  GetWeeklyForecastRequestFilters,
} from '@provider-portal/common/api/gen/CampaignDeliveryProvider';
import { CreatePromotionButton } from '@provider-portal/promotions-common/components/CreatePromotionButton/CreatePromotionButton';
import { EligibilityMetrics } from '@provider-portal/promotions-common/components/EligibilityMeticsCards/EligibilityMetrics';
import { SetupNewPromotionSection } from '@provider-portal/promotions-common/components/SetupNewPromotion/SetupNewPromotionSection';
import { PromotionsFocusGrid } from 'promotions/components/PromotionFocusGrid/PromotionFocusGrid';

import { useSelectedProvider } from 'hooks/useSelectedProvider';

import { useAppServices } from 'components/AppServicesProvider';

export interface SetupNewPromotionFCProps {
  strings: Record<
    | 'title'
    | 'subtitle'
    | 'helpText'
    | 'newPromotion'
    | 'newPromotionSubtitle'
    | 'otherOptionsTitle'
    | 'otherOptionsSubtitle',
    string
  >;
  onClick: (filters?: GetWeeklyForecastRequestFilters, key?: string) => void;
  focuses?: Focus[];
  recommendations?: Focus[];
}
const style = {
  promotions: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    padding: '24px',
    background: '#F4F5F6',
    borderRadius: '16px',
    flex: 'none',
    order: 0,
    marginTop: '24px',
  },
  tooltip: {
    background: '#2F313F',
    width: '280px',
    height: '76px',
    alignItems: 'center',
    display: 'flex',
  },
} as const;

const SetupPromotionFromScratch: React.FC<SetupNewPromotionFCProps> = memo(
  ({ strings, onClick }) => {
    const onFocusClick = useCallback(() => {
      onClick(undefined, 'new_promotion');
    }, [onClick]);
    return (
      <Box sx={style.promotions}>
        <Typography variant="h6" color="textPrimary">
          {strings.newPromotion}
        </Typography>
        <Box mb={3}>
          <Typography variant="bodyM" color="textSecondary">
            {strings.newPromotionSubtitle}
          </Typography>
        </Box>
        <CreatePromotionButton onClick={onFocusClick} />
      </Box>
    );
  },
);

export const SetupNewPromotionFC: React.FC<SetupNewPromotionFCProps> = memo(
  ({ strings, onClick, focuses }) => {
    const selectedProvider = useSelectedProvider();
    const isMetricsBanned = !selectedProvider.permissions.canCreatePromotions;
    const [bannedUntil, setBannedUntil] = useState<number | undefined>(undefined);
    const { providerId } = selectedProvider;
    const { apiClient } = useAppServices();

    useEffect(() => {
      (async () => {
        if (isMetricsBanned) {
          return;
        }
        let data: GetProvidersEligibilityDataResponse;
        try {
          data = await apiClient.promotions.getProviderEligibilityData({
            provider_id: providerId,
          });
        } catch (_e) {
          data = undefined;
        } finally {
          setBannedUntil(data?.banned_until);
        }
      })();
    }, [apiClient, isMetricsBanned, providerId]);

    return (
      <Box>
        <SetupNewPromotionSection
          title={strings.title}
          subtitle={strings.otherOptionsSubtitle}
          helpText={isMetricsBanned ? undefined : strings.helpText}
          isMetricBanned={isMetricsBanned}
          foodBannedUntil={bannedUntil}
        >
          {isMetricsBanned ? (
            <EligibilityMetrics apiClient={apiClient} providerId={providerId} />
          ) : (
            <>
              <PromotionsFocusGrid onClickNewPromotion={onClick} focuses={focuses} />
              <SetupPromotionFromScratch strings={strings} onClick={onClick} />
            </>
          )}
        </SetupNewPromotionSection>
      </Box>
    );
  },
);
